
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuItem, Language, Addon } from '../types';
import { getDrinkPairing, getIngredientInfo } from '../services/geminiService';
import { CloseIcon, FireIcon, LeafIcon, StarIcon, DrinksIcon, InfoIcon } from './icons';
import Loader from './Loader';
import { t } from '../translations';
import { useCart } from '../contexts/CartContext';
import { useSettings } from '../src/contexts/SettingsContext';
import { Plus } from 'lucide-react';
import { DEFAULT_MENU_ITEM_IMAGE } from '../src/constants';
import { formatPrice } from '../src/utils/formatPrice';

interface DishDetailModalProps {
  dish: MenuItem | null;
  onClose: () => void;
  language: Language;
}

const NutritionItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className="text-center p-2 bg-[var(--bg-card)] rounded-2xl shadow-sm">
        <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider font-bold mb-1">{label}</p>
        <p className="font-bold text-[var(--text-primary)]">{value}</p>
    </div>
);

const Tag: React.FC<{tag: string, language: Language}> = ({ tag, language }) => {
    const tagStyles: Record<string, {icon: React.ReactElement, text: string, className: string}> = {
        'best-seller': { icon: <StarIcon className="w-4 h-4"/>, text: t('filters.bestSeller', language), className: "bg-warning-bg-50 text-warning-fg" },
        'spicy': { icon: <FireIcon className="w-4 h-4"/>, text: t('filters.spicy', language), className: "bg-danger-bg-50 text-danger-fg" },
        'vegetarian': { icon: <LeafIcon className="w-4 h-4"/>, text: t('filters.vegetarian', language), className: "bg-success-bg-50 text-success-fg" },
    }
    const style = tagStyles[tag];
    if(!style) return null;

    return (
        <div className={`flex items-center space-x-1.5 text-sm font-bold px-3 py-1.5 rounded-full ${style.className}`}>
            {style.icon}
            <span>{style.text}</span>
        </div>
    )
}

const AddonCheckbox: React.FC<{ addon: Addon, language: Language, currency: string, checked: boolean, onChange: (checked: boolean) => void }> = ({ addon, language, currency, checked, onChange }) => (
    <label className="flex items-center justify-between p-3 bg-[var(--bg-tertiary)] rounded-2xl cursor-pointer hover:bg-opacity-80 transition-colors">
        <span className="text-[var(--text-primary)] font-medium">{addon.names?.[language] || addon.names?.ru}</span>
        <div className="flex items-center space-x-3">
             <span className="font-bold text-[var(--text-primary)]">+{addon.price} {currency}</span>
            <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-5 w-5 rounded-full text-[var(--accent-color)] bg-[var(--bg-tertiary)] border-transparent focus:ring-[var(--accent-color)]" />
        </div>
    </label>
);


import { toast } from 'sonner';
import { useRecentlyViewed } from '../src/utils/useRecentlyViewed';

const DishDetailModal: React.FC<DishDetailModalProps> = ({ dish, onClose, language }) => {
  const { addToCart } = useCart();
  const { settings } = useSettings();
  const { visit } = useRecentlyViewed();
  const [pairing, setPairing] = useState<string | null>(null);
  const [ingredientInfo, setIngredientInfo] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<null | 'pairing' | 'ingredients'>(null);
  const [selectedAddons, setSelectedAddons] = useState<Addon[]>([]);

  // Reset addons when dish changes + запоминаем в recently-viewed
  React.useEffect(() => {
    setSelectedAddons([]);
    setPairing(null);
    setIngredientInfo(null);
    if (dish?.id) visit(dish.id);
  }, [dish, visit]);

  const addonTotal = selectedAddons.reduce((sum, a) => sum + a.price, 0);
  const totalWithAddons = (dish?.price || 0) + addonTotal;

  const handleGetPairing = async () => {
    if (!dish) return;
    setIsLoading('pairing');
    setPairing(null);
    setIngredientInfo(null);
    try {
      const result = await getDrinkPairing(dish, language);
      setPairing(result);
    } catch (error) {
      console.error(error);
      toast.error(t('dishModal.pairingError', language));
    } finally {
      setIsLoading(null);
    }
  };

  const handleGetIngredientInfo = async () => {
    if (!dish) return;
    setIsLoading('ingredients');
    setIngredientInfo(null);
    setPairing(null);
    try {
      const result = await getIngredientInfo(dish.names?.[language] || dish.names?.ru, language);
      setIngredientInfo(result);
    } catch (error) {
      console.error(error);
      toast.error(t('dishModal.ingredientsError', language));
    } finally {
      setIsLoading(null);
    }
  };

  return (
    <AnimatePresence>
      {dish && (
        <motion.div
          key="dish-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center bg-black/60 backdrop-blur-sm"
        >
          <motion.div
            key="dish-modal-content"
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            onDragEnd={(_, info) => {
              if (info.offset.y > 100 || info.velocity.y > 500) {
                onClose();
              }
            }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[var(--bg-secondary)] w-full max-w-lg rounded-t-[30px] sm:rounded-[30px] shadow-[var(--shadow-medium)] overflow-hidden max-h-[90dvh] flex flex-col"
          >
            {/* Drag indicator for mobile */}
            <div className="flex justify-center pt-2 pb-0 sm:hidden">
              <div className="w-10 h-1 bg-[var(--text-secondary)] rounded-full opacity-30"></div>
            </div>
            <div className="relative h-56 sm:h-64 flex-shrink-0">
              <img
                src={dish.image || DEFAULT_MENU_ITEM_IMAGE}
                alt={dish.names?.[language] || dish.names?.ru}
                loading="lazy"
                decoding="async"
                className={`w-full h-full object-cover ${dish.inStock === false ? 'grayscale-[0.5] opacity-75' : ''}`}
                referrerPolicy="no-referrer"
                onError={(e) => {
                    (e.target as HTMLImageElement).src = DEFAULT_MENU_ITEM_IMAGE;
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              
              {dish.isPopular && (
                <div className="absolute top-4 left-4 bg-warning text-white text-xs font-black px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-xl z-10">
                  <StarIcon className="w-4 h-4 fill-current" />
                  <span>{t('dishModal.popular', language).toUpperCase()}</span>
                </div>
              )}

              {dish.inStock === false && (
                <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-20">
                  <div className="bg-white/90 text-danger-fg text-sm font-black px-4 py-2 rounded-full shadow-2xl transform -rotate-6 border-2 border-danger">
                    {t('dishModal.outOfStock', language).toUpperCase()}
                  </div>
                </div>
              )}

              <button
                onClick={onClose}
                className="absolute top-3 right-3 bg-black/40 backdrop-blur-md text-white p-2.5 rounded-full hover:bg-black/60 transition-colors z-30 safe-top"
                style={{ marginTop: 'env(safe-area-inset-top, 0px)' }}
                aria-label="Close"
              >
                <CloseIcon className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 overscroll-contain">
              <div className="flex justify-between items-start mb-2 gap-4">
                <h2 className="text-3xl font-bold font-serif text-[var(--text-primary)] flex-1 leading-tight">
                  {dish.names?.[language] || dish.names?.ru}
                </h2>
                {/* Цена не CTA — не должна выглядеть как кнопка.
                    Простое крупное число, без оранжевого pill-а. */}
                <div className="text-xl font-black text-[var(--text-primary)] whitespace-nowrap pt-1">
                    {formatPrice(dish.price, settings.currency)}
                </div>
              </div>
              
              <div className="flex items-center space-x-2 mt-2 mb-4">
                {dish.tags?.map(tag => <Tag key={tag} tag={tag} language={language} />)}
              </div>
              
              <p className="text-[var(--text-secondary)] leading-relaxed text-base">
                {dish.descriptions[language]}
              </p>

              {dish.ingredients?.[language] && (
                <div className="mt-4 p-4 bg-[var(--bg-tertiary)] rounded-2xl border border-[var(--glass-border)]">
                  <h3 className="font-bold text-[var(--text-secondary)] mb-2 text-[10px] uppercase tracking-widest">{t('dishModal.ingredients', language)}</h3>
                  <p className="text-sm text-[var(--text-primary)] leading-relaxed">
                    {dish.ingredients[language]}
                  </p>
                </div>
              )}

              {dish.addons && (
                  <div className="mt-8">
                      <h3 className="font-bold text-[var(--text-secondary)] mb-3 text-xs uppercase tracking-widest">{t('dishModal.addons', language)}</h3>
                      <div className="space-y-2">
                        {dish.addons.map((addon, index) => (
                          <AddonCheckbox
                            key={index}
                            addon={addon}
                            language={language}
                            currency={settings.currency}
                            checked={selectedAddons.some(a => a.names.en === addon.names.en)}
                            onChange={(checked) => {
                              setSelectedAddons(prev =>
                                checked
                                  ? [...prev, addon]
                                  : prev.filter(a => a.names.en !== addon.names.en)
                              );
                            }}
                          />
                        ))}
                      </div>
                  </div>
              )}
              
              <div className="mt-8 grid grid-cols-3 gap-3">
                <motion.button whileTap={{scale: 0.97}} onClick={handleGetPairing} disabled={!!isLoading} className="flex flex-col items-center justify-center p-4 bg-[var(--bg-tertiary)] rounded-3xl hover:bg-opacity-80 transition-colors disabled:opacity-50">
                  <DrinksIcon className="w-6 h-6 text-[var(--accent-color)] mb-2"/>
                  <span className="text-xs font-bold text-[var(--text-primary)] text-center">{t('dishModal.getPairing', language)}</span>
                </motion.button>
                <motion.button whileTap={{scale: 0.97}} onClick={handleGetIngredientInfo} disabled={!!isLoading} className="flex flex-col items-center justify-center p-4 bg-[var(--bg-tertiary)] rounded-3xl hover:bg-opacity-80 transition-colors disabled:opacity-50">
                  <InfoIcon className="w-6 h-6 text-[var(--accent-color)] mb-2"/>
                  <span className="text-xs font-bold text-[var(--text-primary)] text-center">{t('dishModal.getIngredients', language)}</span>
                </motion.button>
                <motion.button whileTap={{scale: 0.97}} onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: dish.names?.[language] || dish.names?.ru,
                      text: `${dish.names?.[language] || dish.names?.ru} — ${dish.price} ${settings.currency}`,
                      url: window.location.href
                    }).catch(() => {});
                  }
                }} className="flex flex-col items-center justify-center p-4 bg-[var(--bg-tertiary)] rounded-3xl hover:bg-opacity-80 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-[var(--accent-color)] mb-2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
                  </svg>
                  <span className="text-xs font-bold text-[var(--text-primary)] text-center">{language === 'ru' ? 'Поделиться' : language === 'ky' ? 'Бөлүшүү' : language === 'zh' ? '分享' : 'Share'}</span>
                </motion.button>
              </div>

                <AnimatePresence>
                  {(isLoading || pairing || ingredientInfo) && (
                    <motion.div 
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4 bg-[var(--bg-tertiary)] rounded-3xl overflow-hidden">
                      <div className="p-5 text-center">
                        {isLoading && (
                          <div className="space-y-2">
                            <div className="h-3 w-24 mx-auto bg-[var(--bg-card)] rounded animate-pulse"></div>
                            <div className="h-5 w-40 mx-auto bg-[var(--bg-card)] rounded animate-pulse"></div>
                            <div className="h-3 w-32 mx-auto bg-[var(--bg-card)] rounded animate-pulse"></div>
                          </div>
                        )}
                        {pairing && (
                            <div>
                                <p className="text-xs text-[var(--text-secondary)] uppercase tracking-wider mb-1">{t('dishModal.recommendedPairing', language)}</p>
                                <p className="font-bold text-[var(--text-primary)] text-lg">{pairing}</p>
                            </div>
                        )}
                        {ingredientInfo && <p className="text-sm text-[var(--text-secondary)] leading-relaxed italic">"{ingredientInfo}"</p>}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

              {dish.nutrition && (
                  <div className="mt-8">
                      <h3 className="font-bold text-[var(--text-secondary)] mb-3 text-xs uppercase tracking-widest text-center">{t('dishModal.nutrition', language)}</h3>
                      <div className="grid grid-cols-4 gap-2">
                          <NutritionItem label={t('dishModal.calories', language)} value={dish.nutrition.calories} />
                          <NutritionItem label={t('dishModal.protein', language)} value={dish.nutrition.protein} />
                          <NutritionItem label={t('dishModal.fat', language)} value={dish.nutrition.fat} />
                          <NutritionItem label={t('dishModal.carbs', language)} value={dish.nutrition.carbs} />
                      </div>
                  </div>
              )}
              
              {dish.allergens?.[language] && (
                  <div className="mt-6 mb-4 text-xs text-[var(--text-secondary)] text-center bg-[var(--bg-tertiary)] py-2 px-4 rounded-full inline-block mx-auto w-full">
                      <span className="font-bold">{t('dishModal.allergens', language)}:</span> {dish.allergens[language]}
                  </div>
              )}

              <div className="mt-8 sticky bottom-0 bg-[var(--bg-secondary)] pt-4 pb-2">
                {dish.inStock === false ? (
                  <div className="w-full py-4 bg-[var(--neutral-bg)] text-[var(--text-muted)] font-bold rounded-2xl text-center uppercase tracking-widest border-2 border-dashed border-[var(--glass-border)]">
                    {t('dishModal.outOfStock', language)}
                  </div>
                ) : (
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      if (dish) {
                        addToCart(dish, selectedAddons.length > 0 ? selectedAddons : undefined);
                        onClose();
                      }
                    }}
                    className="w-full py-4 bg-[var(--accent-color)] text-white font-bold rounded-2xl shadow-xl flex items-center justify-center gap-3 hover:opacity-90 transition-opacity"
                  >
                    <Plus className="w-5 h-5" />
                    <span>{t('cart.add', language)}</span>
                    <span className="opacity-80">·</span>
                    <span>{formatPrice(totalWithAddons, settings.currency)}</span>
                  </motion.button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DishDetailModal;
